import { useCallback, useMemo, useRef, useState } from 'react';

import { Box as Flex, MenuItem, Tooltip, makeStyles } from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiGroupLine,
  RiImageAddLine,
  RiImageEditLine,
  RiLayoutGridLine,
} from '@remixicon/react';
import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import classNames from 'classnames';

import {
  useBlockZoneId,
  useCardDelete,
  useCardFocusTracking,
  useImageUpload,
  useIsEditorEditable,
} from '@spotify/confidence-editor-react';
import { Treatment } from '@spotify/confidence-editor-react';
import { BlockReorderButtons } from '@spotify/confidence-editor-react';
import { BlockCommentButton } from '@spotify/confidence-editor-react';
import { ToolbarButton } from '@spotify/confidence-editor-react';
import { ToolbarCard } from '@spotify/confidence-editor-react';
import { ToolbarSelect } from '@spotify/confidence-editor-react';
import {
  ImageCard,
  useImageCardStyles,
} from '@spotify/confidence-editor-react';
import { CARD_CONTENT } from '@spotify/confidence-editor-react';
import {
  tiptapBlockMarginBottom,
  tiptapBlockMarginTop,
} from '@spotify/confidence-editor-react';
import { closeAllDropdowns } from '@spotify/confidence-editor-react';
import {
  CommentZone,
  CommentZoneProvider,
} from '@spotify/plugin-confidence-comments-react';

import { useReportBlockData } from '../ReportBlockDataContext';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variantCard: {
      setVariantCard: () => ReturnType;
    };
  }
}

const useVariantCardStyles = makeStyles(() => ({
  container: {
    width: '100%',
    marginBottom: tiptapBlockMarginBottom,
    marginTop: tiptapBlockMarginTop,
  },
  emptyState: {
    alignItems: 'center',
    backgroundColor: 'var(--bui-bg-neutral-2)',
    borderRadius: 'var(--bui-radius-4)',
    color: 'var(--bui-fg-disabled)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
    justifyContent: 'center',
    minHeight: 200,
    padding: 'var(--bui-space-6)',
    width: '100%',
  },
  emptyStateText: {
    fontSize: 'var(--bui-font-size-3)',
    fontWeight: 500,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
  },
  variantName: {
    color: 'var(--bui-fg-primary)',
    fontSize: 'var(--bui-font-size-4)',
    fontWeight: 600,
    margin: 0,
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--bui-space-1)',
    paddingLeft: 'var(--bui-space-2)',
    paddingRight: 'var(--bui-space-3)',
    backgroundColor: 'var(--bui-bg-neutral-2)',
    borderRadius: 'var(--bui-radius-2)',
    fontSize: 'var(--bui-font-size-2)',
    fontWeight: 500,
    color: 'var(--bui-fg-primary)',
    border: '1px solid var(--bui-border)',
    whiteSpace: 'nowrap' as const,
    margin: 0,
    height: 34,
  },
  tagWeight: {
    fontWeight: 700,
  },
  tagLabel: {
    color: 'var(--bui-fg-secondary)',
  },
}));

export const buildVariantCardContent = (treatment: Treatment) => ({
  type: 'variantCard',
  attrs: {
    treatmentName: treatment.name,
    src: treatment.imageUri || null,
  },
  content: [
    {
      type: 'paragraph',
      content: treatment.description
        ? [{ type: 'text', text: treatment.description }]
        : [{ type: 'text', text: 'Add description...' }],
    },
  ],
});

const VariantCardComponent = ({
  node,
  updateAttributes,
  editor,
  getPos,
}: NodeViewProps) => {
  const isEditable = useIsEditorEditable(editor);
  const cardRef = useRef<HTMLDivElement>(null);
  const classes = useImageCardStyles();
  const variantClasses = useVariantCardStyles();
  const { treatments } = useReportBlockData();
  const isFocused = useCardFocusTracking(editor, getPos, node.nodeSize);
  const { fileInputRef, openFileDialog, handleFileSelect } =
    useImageUpload(updateAttributes);
  const handleDelete = useCardDelete(editor, getPos, node.nodeSize);

  const { src, treatmentName } = node.attrs;
  const zoneId = useBlockZoneId('variant-card', treatmentName);

  const treatment = treatments?.find(t => t.name === treatmentName);

  const renderImage = () => {
    if (!isEditable) {
      return src ? (
        <img src={src} alt="Variant" className={classes.image} />
      ) : (
        <div className={classes.imagePlaceholderStatic}>
          <RiLayoutGridLine size={32} />
          <span>No image</span>
        </div>
      );
    }

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className={classes.hiddenFileInput}
        />
        {src ? (
          <button
            type="button"
            onClick={openFileDialog}
            className={classes.imageButton}
            aria-label="Change image"
          >
            <img src={src} alt="Variant" className={classes.image} />
            <div className={classes.imageOverlay} data-image-overlay>
              <RiImageEditLine size={32} />
            </div>
          </button>
        ) : (
          <button
            type="button"
            className={classes.imagePlaceholder}
            onClick={openFileDialog}
          >
            <RiImageAddLine size={32} />
            <span style={{ marginTop: 8 }}>Click to upload an image</span>
          </button>
        )}
      </>
    );
  };

  const handleSelectVariant = useCallback(
    (newTreatmentName: string) => {
      const selectedTreatment = treatments?.find(
        t => t.name === newTreatmentName,
      );
      if (!selectedTreatment) return;
      const pos = getPos();
      if (typeof pos !== 'number') return;
      editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + node.nodeSize })
        .insertContentAt(pos, buildVariantCardContent(selectedTreatment))
        .run();
    },
    [editor, getPos, node.nodeSize, treatments],
  );

  const [isToolbarActive, setIsToolbarActive] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsToolbarActive(open);
      if (open) {
        closeAllDropdowns();
        const { to } = editor.state.selection;
        editor.commands.setTextSelection(to);
      }
    },
    [editor],
  );

  const selectOptions = useMemo(
    () =>
      (treatments ?? []).map(t => ({
        label: t.displayName || t.name,
        value: t.name,
        description:
          t.weight !== undefined ? `${t.weight / 100}% of traffic` : undefined,
      })),
    [treatments],
  );

  const renderTrafficTag = () => {
    if (treatment?.weight === undefined) return null;
    return (
      <div contentEditable={false}>
        <span className={variantClasses.tag}>
          <RiGroupLine size={16} />
          <span className={variantClasses.tagWeight}>
            {treatment.weight / 100}%
          </span>
          <span className={variantClasses.tagLabel}>of traffic split</span>
        </span>
      </div>
    );
  };

  if (!treatmentName) {
    return (
      <NodeViewWrapper>
        <div className={variantClasses.container}>
          <div ref={cardRef} className={variantClasses.emptyState}>
            <RiLayoutGridLine size={32} />
            <span className={variantClasses.emptyStateText}>
              Select a variant
            </span>
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  const displayName =
    treatment?.displayName || treatment?.name || treatmentName;

  const content = (
    <div
      ref={cardRef}
      className={classNames(classes.card, {
        [classes.cardFocused]: isFocused || isToolbarActive,
      })}
    >
      {isEditable && (
        <div contentEditable={false} className={classes.hoverToolbar}>
          <ToolbarCard>
            <Flex
              display="flex"
              alignItems="center"
              style={{
                gap: 'var(--bui-space-0_5)',
              }}
            >
              <BlockReorderButtons getPos={getPos} editor={editor} />
              <ToolbarSelect
                aria-label="Select variant"
                name="variant-card-hover-select"
                value={treatmentName}
                onChange={e => {
                  const newValue = e.target.value;
                  if (!newValue) return;
                  handleSelectVariant(String(newValue));
                }}
                onOpen={() => handleOpenChange(true)}
                onClose={() => handleOpenChange(false)}
                disabled={(treatments ?? []).length === 0}
              >
                {selectOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </ToolbarSelect>
              {treatmentName && (
                <CommentZoneProvider zone={`variant-card/${treatmentName}`}>
                  <BlockCommentButton />
                </CommentZoneProvider>
              )}
              <Tooltip title="Delete variant card">
                <ToolbarButton
                  aria-label="Delete variant card"
                  onClick={handleDelete}
                >
                  <RiDeleteBinLine />
                </ToolbarButton>
              </Tooltip>
            </Flex>
          </ToolbarCard>
        </div>
      )}
      <div className={classes.imageContainer}>{renderImage()}</div>
      <div className={classes.contentContainer}>
        <div className={variantClasses.contentWrapper}>
          <h3 className={variantClasses.variantName} contentEditable={false}>
            {displayName}
          </h3>
          <NodeViewContent />
          {renderTrafficTag()}
        </div>
      </div>
    </div>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const VariantCard = ImageCard.extend({
  name: 'variantCard',
  content: CARD_CONTENT,

  addAttributes() {
    return {
      ...this.parent?.(),
      treatmentName: {
        default: null,
        parseHTML: element => element.getAttribute('data-treatment-name'),
        renderHTML: attributes => ({
          'data-treatment-name': attributes.treatmentName,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="variant-card"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', { ...HTMLAttributes, 'data-type': 'variant-card' }, 0];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(VariantCardComponent);
  },

  addCommands() {
    return {
      setVariantCard:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            content: [
              {
                type: 'paragraph',
              },
            ],
          });
        },
    };
  },
});
